import { IDDocumentType } from "@amzn/client-workbench-api-model";
import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Col, Row, View } from "@amzn/stencil-react-components/layout";
import {
  MessageBanner,
  MessageBannerType,
} from "@amzn/stencil-react-components/message-banner";
import { H3, Text } from "@amzn/stencil-react-components/text";
import IconCheckCircleFillSmall from "@amzn/stencil-react-icons/icons/icon-check-circle-fill-small";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { AppContext } from "@/stores/appStore";

/** How long the page must stop resizing before we scroll to the gate. */
const LAYOUT_QUIET_MS = 200;
/** Scroll anyway after this long, in case something keeps resizing the page. */
const LAYOUT_MAX_WAIT_MS = 2000;

interface ConfirmIdImageProps {
  /** The document step whose captured still should be confirmed. */
  documentType: IDDocumentType;
  /** Whether document still capture is active for this step. */
  enabled: boolean;
  /** Fired when the new hire confirms the still is legible (for metrics). */
  onConfirm?: () => void;
  /** Discards the recording and resets the recorder for a fresh take. */
  onRetake: () => void;
  /** The submit affordance, revealed only after the new hire confirms. */
  children: JSX.Element;
}

/**
 * Shows the still image captured from the ID recording and asks the new hire to
 * confirm the required details are legible before revealing the submit button.
 * The point is to catch blurry/unreadable captures at the source rather than
 * leaving a human verifier to scrub the video.
 *
 * When capture is disabled or no still was captured (e.g. the OpenCV runtime
 * failed to load), this transparently renders `children`, preserving the
 * video-only flow.
 *
 * A "fallback" still means no frame was steady and sharp enough, so it is the
 * likeliest to be unreadable. Capture has no card detection, so we can't say
 * the ID is missing; we lead with recording again instead of confirming.
 */
export default function ConfirmIdImage({
  documentType,
  enabled,
  onConfirm,
  onRetake,
  children,
}: ConfirmIdImageProps): JSX.Element {
  const { state } = useContext(AppContext);
  const intl = useIntl();
  const [confirmed, setConfirmed] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const headingId = `confirmIdImageHeading-${documentType}`;

  const doc = state.documents?.find((d) => d.documentType === documentType);
  const idImageBlob = doc?.idImageBlob;
  const isFallback = doc?.idImageCaptureSource === "fallback";
  const showGate = enabled && Boolean(idImageBlob) && !confirmed;

  const objectUrl = useMemo(
    () => (idImageBlob ? URL.createObjectURL(idImageBlob) : undefined),
    [idImageBlob]
  );

  // Revoke the object URL when it changes or on unmount to avoid leaking the
  // captured ID image (sensitive PII) in memory.
  useEffect(() => {
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [objectUrl]);

  // The gate replaces the submit button and renders below the recorded video,
  // usually below the fold. Move focus to it so the new hire (and screen
  // readers) land on the next required step.
  useEffect(() => {
    if (showGate) {
      containerRef.current?.focus({ preventScroll: true });
    }
  }, [showGate]);

  // Scroll once the page stops resizing: the recorder's replay area above the
  // gate animates open (~1s) after recording, so scrolling right away lands
  // short and the growing content pushes the gate back below the fold.
  const scrollGateIntoView = () =>
    containerRef.current?.scrollIntoView?.({
      behavior: "smooth",
      block: "start",
    });
  const cancelPendingScrollRef = useRef<() => void>();
  const scrollGateIntoViewWhenSettled = () => {
    cancelPendingScrollRef.current?.();
    if (typeof ResizeObserver === "undefined") {
      scrollGateIntoView();
      return;
    }
    let quietTimer = setTimeout(scrollNow, LAYOUT_QUIET_MS);
    const maxWaitTimer = setTimeout(scrollNow, LAYOUT_MAX_WAIT_MS);
    const observer = new ResizeObserver(() => {
      clearTimeout(quietTimer);
      quietTimer = setTimeout(scrollNow, LAYOUT_QUIET_MS);
    });
    observer.observe(document.documentElement);

    function cancel() {
      clearTimeout(quietTimer);
      clearTimeout(maxWaitTimer);
      observer.disconnect();
      cancelPendingScrollRef.current = undefined;
    }
    function scrollNow() {
      cancel();
      scrollGateIntoView();
    }
    cancelPendingScrollRef.current = cancel;
  };

  useEffect(() => () => cancelPendingScrollRef.current?.(), []);

  if (!enabled || !idImageBlob) {
    return children;
  }

  if (confirmed) {
    return (
      <Col gridGap="S300">
        <Row gridGap="S200" alignItems="center">
          <IconCheckCircleFillSmall
            aria-hidden={true}
            color="color.status.success-icon"
          />
          <Text dataTestId="confirmIdImageConfirmed">
            <FormattedMessage id="identityCheck-confirmIdImage-confirmed" />
          </Text>
        </Row>
        {children}
      </Col>
    );
  }

  return (
    <div
      ref={containerRef}
      tabIndex={-1}
      role="region"
      aria-labelledby={headingId}
      style={{ outline: "none" }}
    >
      <View
        dataTestId="confirmIdImage"
        padding={{ top: "S300", bottom: "S300" }}
        backgroundColor="color.surface.bg-muted"
        borderRadius="dimensions.border.radius.200"
      >
        <Col gridGap="S300">
          <H3
            id={headingId}
            fontSize="T400"
            fontWeight="bold"
            color="color.text.primary"
          >
            <FormattedMessage id="identityCheck-confirmIdImage-heading" />
          </H3>
          <MessageBanner type={MessageBannerType.Informational}>
            <Text dataTestId="confirmIdImagePrompt">
              <FormattedMessage
                id={
                  isFallback
                    ? "identityCheck-confirmIdImage-fallbackPrompt"
                    : "identityCheck-confirmIdImage-prompt"
                }
              />
            </Text>
          </MessageBanner>
          <View
            border="1px solid color.border.primary"
            borderRadius="dimensions.border.radius.100"
            style={{ overflow: "hidden" }}
          >
            <img
              src={objectUrl}
              alt={intl.formatMessage({
                id: "identityCheck-confirmIdImage-alt",
              })}
              data-test-id="confirmIdImagePreview"
              onLoad={scrollGateIntoViewWhenSettled}
              style={{ display: "block", width: "100%" }}
            />
          </View>
          <Row gridGap="S300" flexWrap="wrap">
            <Button
              variant={
                isFallback ? ButtonVariant.Secondary : ButtonVariant.Primary
              }
              dataTestId="confirmIdImageBtn"
              onClick={() => {
                onConfirm?.();
                setConfirmed(true);
              }}
            >
              <FormattedMessage id="identityCheck-confirmIdImage-confirm" />
            </Button>
            <Button
              variant={
                isFallback ? ButtonVariant.Primary : ButtonVariant.Secondary
              }
              dataTestId="confirmIdImageRetakeBtn"
              onClick={onRetake}
            >
              <FormattedMessage id="identityCheck-confirmIdImage-retake" />
            </Button>
          </Row>
        </Col>
      </View>
    </div>
  );
}
